import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Npc } from '../types';
import { addNpcToGame, fetchGameNpcs, fetchNpcs, removeNpcFromGame, revokeNpcEffect, setGameNpcHealth, triggerNpcEffect } from '../api/npcs';
import { burnTriggerAmount, isBurnEffect } from '../utils/spellEffect';
import { ConfirmDialog } from './ConfirmDialog';
import { VitalSliderDialog } from './VitalSliderDialog';
import { barPercent } from '../utils/avatars';
import { NpcMark } from './Pictogram';

interface GameNpcsSectionProps {
  gameId: number;
  compact?: boolean;
}

const MAX_COPIES = 20;

function clampCount(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(MAX_COPIES, Math.trunc(value)));
}

function npcVitals(npc: Npc) {
  const stored = npc.health ?? 0;
  const storedMax = npc.max_health ?? 0;
  const maxHealth = npc.scaled_max_health ?? storedMax;
  const health = storedMax > 0 && stored === storedMax ? maxHealth : Math.min(stored, maxHealth || stored);
  return { health, maxHealth: maxHealth || storedMax };
}

export function GameNpcsSection({ gameId, compact = false }: GameNpcsSectionProps) {
  const [npcs, setNpcs] = useState<Npc[]>([]);
  const [templates, setTemplates] = useState<Npc[]>([]);
  const [context, setContext] = useState({ level: 1, player_count: 0 });
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [toRemove, setToRemove] = useState<Npc | null>(null);
  const [hpEdit, setHpEdit] = useState<Npc | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const pickerRef = useRef(false);
  const hpEditRef = useRef<Npc | null>(null);
  const removeRef = useRef<Npc | null>(null);
  busyRef.current = busy;
  pickerRef.current = pickerOpen;
  hpEditRef.current = hpEdit;
  removeRef.current = toRemove;

  const handleTriggerEffect = async (npc: Npc, effectId: number) => {
    setBusy(true);
    try {
      await triggerNpcEffect(gameId, npc.id, effectId);
      await load();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Effekt konnte nicht ausgelöst werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeEffect = async (npc: Npc, effectId: number) => {
    setBusy(true);
    try {
      await revokeNpcEffect(gameId, npc.id, effectId);
      await load();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Effekt konnte nicht beendet werden.');
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async (background = false) => {
    if (!background) setLoadError(null);
    try {
      const data = await fetchGameNpcs(gameId);
      setNpcs(Array.isArray(data?.npcs) ? data.npcs : []);
      if (data?.scaling_context) setContext(data.scaling_context);
      if (background) setLoadError(null);
    } catch (err) {
      if (background) return;
      setLoadError(err instanceof Error ? err.message : 'Gegner konnten nicht geladen werden.');
    }
  }, [gameId]);

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (busyRef.current || pickerRef.current || hpEditRef.current || removeRef.current) return;
      void load(true);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [load]);

  const closePicker = () => {
    setPickerOpen(false);
    setCounts({});
  };

  const openPicker = async () => {
    setLoadError(null);
    try {
      setTemplates(await fetchNpcs());
      setCounts({});
      setPickerOpen(true);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Vorlagen konnten nicht geladen werden.');
    }
  };

  const setCount = (templateId: number, value: number) => {
    setCounts((current) => ({ ...current, [templateId]: clampCount(value) }));
  };

  const toggleTemplate = (templateId: number) => {
    setCounts((current) => ({
      ...current,
      [templateId]: (current[templateId] || 0) > 0 ? 0 : 1,
    }));
  };

  const saveHealth = async (value: number) => {
    if (!hpEdit) return;
    setBusy(true);
    setLoadError(null);
    try {
      await setGameNpcHealth(gameId, hpEdit.id, value);
      setHpEdit(null);
      await load();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Leben konnte nicht gesetzt werden.');
    } finally {
      setBusy(false);
    }
  };

  const picked = Object.entries(counts).filter((entry): entry is [string, number] => entry[1] > 0);
  const totalSelected = picked.reduce((sum, [, count]) => sum + count, 0);

  const addSelected = async () => {
    if (picked.length === 0) return;
    setBusy(true);
    try {
      for (const [templateId, count] of picked) {
        await addNpcToGame(gameId, Number(templateId), count);
      }
      await load();
      closePicker();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Gegner konnte nicht hinzugefügt werden.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={compact ? 'roster-block game-npcs-compact' : 'overview-section game-npcs-section'}>
      <div className="section-header-with-actions">
        <h2 title={compact ? 'Nur für dich als Spielleiter sichtbar. Spieler sehen diese Liste nicht.' : undefined}>
          Gegner ({npcs.length})
        </h2>
        <button type="button" className="medieval-button small-button" onClick={() => void openPicker()}>
          + Gegner ins Spiel
        </button>
      </div>
      {!compact && (
        <p className="section-hint">Nur für dich als Spielleiter sichtbar. Spieler sehen diese Liste nicht.</p>
      )}
      {loadError && <p className="form-error" role="alert">{loadError}</p>}
      {npcs.length === 0 ? (
        <p className="no-content">Noch keine Gegner in diesem Spiel.</p>
      ) : compact ? (
        <div className="npc-dense-grid">
          {npcs.map(npc => {
            const { health, maxHealth } = npcVitals(npc);
            const damage = npc.scaled_damage ?? npc.damage;
            const movement = npc.scaled_movement ?? npc.movement;
            return (
              <div
                key={npc.id}
                className="npc-dense-row"
                title={`${npc.name}: HP ${health}/${maxHealth}, Schaden ${damage}, Bewegung ${movement}`}
              >
                <span className="npc-dense-mark" aria-hidden>
                  <NpcMark icon={npc.icon} iconUrl={npc.icon_url} npcType={npc.npc_type} />
                </span>
                <button
                  type="button"
                  className="npc-dense-main"
                  aria-label={`HP von ${npc.name} setzen`}
                  onClick={() => setHpEdit(npc)}
                >
                  <strong>{npc.name}</strong>
                  <span>HP {health}/{maxHealth}</span>
                  <span className="npc-dense-dmg">⚔ {damage}</span>
                </button>
                <button
                  type="button"
                  className="npc-dense-remove"
                  onClick={() => setToRemove(npc)}
                  aria-label="Entfernen"
                  title="Aus Spiel nehmen"
                >
                  ✕
                </button>
                {(npc.effects || []).length > 0 && (
                  <div className="combat-effect-list npc-dense-effects">
                    {(npc.effects || []).map((effect) => (
                      <div key={effect.id} className="combat-effect-chip">
                        <span>{effect.name}</span>
                        {isBurnEffect(effect) && (
                          <button type="button" className="combat-effect-btn" disabled={busy} onClick={() => void handleTriggerEffect(npc, effect.id)} title="Schaden auslösen" aria-label={`${effect.name} auslösen`}>
                            🔥{burnTriggerAmount(effect) > 0 ? ` ${burnTriggerAmount(effect)}` : ''}
                          </button>
                        )}
                        <button type="button" className="combat-effect-btn" disabled={busy} onClick={() => void handleRevokeEffect(npc, effect.id)} title="Effekt beenden">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="game-npc-list combat-card-grid">
          {npcs.map(npc => {
            const { health, maxHealth } = npcVitals(npc);
            const damage = npc.scaled_damage ?? npc.damage;
            const movement = npc.scaled_movement ?? npc.movement;
            const hp = barPercent(health, maxHealth);
            return (
            <article key={npc.id} className="combat-card npc npc-card">
              <div className="combat-card-avatar" aria-hidden>
                <NpcMark icon={npc.icon} iconUrl={npc.icon_url} npcType={npc.npc_type} />
              </div>
              <div className="combat-card-body">
              <header>
                <strong>{npc.name}</strong>
                {npc.npc_type && <span className="combat-card-class">{npc.npc_type}</span>}
              </header>
              <button
                type="button"
                className="combat-bar combat-hp is-settable"
                aria-label={`HP von ${npc.name} setzen`}
                onClick={() => setHpEdit(npc)}
              >
                <div className="combat-bar-fill combat-hp-fill" style={{ width: `${hp}%` }} />
                <span>HP {health}/{maxHealth}</span>
              </button>
              <div className="combat-stats">
                <span className="combat-stat">⚔ Schaden {damage}{npc.scaling_mode && npc.scaling_mode !== 'none' && npc.scaled_damage !== npc.damage ? ` (Basis ${npc.damage})` : ''}</span>
                <span className="combat-stat">➤ Bewegung {movement}</span>
              </div>
              {(npc.abilities || []).length > 0 && (
                <p className="combat-kit">
                  Fähigkeiten: {(npc.abilities || []).map(ability => (
                    `${ability.name}${ability.result != null ? ` · ${ability.result}` : ''}`
                  )).join(', ')}
                </p>
              )}
              {(npc.effects || []).length > 0 && (
                <div className="combat-effect-list">
                  {(npc.effects || []).map((effect) => (
                    <div key={effect.id} className="combat-effect-chip">
                      <span>{effect.name}{effect.effect ? ` · ${effect.effect}` : ''}</span>
                      {isBurnEffect(effect) && (
                        <button type="button" className="combat-effect-btn" disabled={busy} onClick={() => void handleTriggerEffect(npc, effect.id)} title="Schaden auslösen" aria-label={`${effect.name} auslösen`}>
                          🔥{burnTriggerAmount(effect) > 0 ? ` ${burnTriggerAmount(effect)}` : ''}
                        </button>
                      )}
                      <button type="button" className="combat-effect-btn" disabled={busy} onClick={() => void handleRevokeEffect(npc, effect.id)} title="Effekt beenden">✕</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="card-actions">
                <button type="button" className="delete-button" onClick={() => setToRemove(npc)}>
                  Aus Spiel nehmen
                </button>
              </div>
              </div>
            </article>
            );
          })}
        </div>
      )}
      {pickerOpen && (
        <div className="modal-overlay" data-gamepad-overlay="modal" onClick={closePicker} role="presentation">
          <div
            className="modal-content game-create-modal npc-picker-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-npc-title"
          >
            <div className="modal-header">
              <h2 id="add-npc-title">Gegner ins Spiel</h2>
              <button type="button" className="modal-close" data-gamepad-dismiss onClick={closePicker} aria-label="Schließen">✕</button>
            </div>
            <div className="modal-body">
              <p className="section-info">
                Nach Anzahl Spieler und nach Level nutzen Durchschnittslevel {context.level} und {context.player_count} Spieler. Nach Helden zählt erst der laufende Kampf. Spieler auf der Seite des Monsters zählen nicht.
              </p>
              {templates.length === 0 ? (
                <p>Keine Vorlagen. Lege Gegner zuerst unter „Gegner“ im Admin-Panel an.</p>
              ) : (
                <ul className="npc-picker-list">
                  {templates.map(template => {
                    const count = counts[template.id] || 0;
                    const selected = count > 0;
                    return (
                      <li key={template.id} className={selected ? 'is-selected' : undefined}>
                        <button
                          type="button"
                          className="npc-picker-option"
                          aria-pressed={selected}
                          onClick={() => toggleTemplate(template.id)}
                        >
                          <span className="npc-picker-check" aria-hidden>{selected ? '✓' : ''}</span>
                          <NpcMark
                            icon={template.icon}
                            iconUrl={template.icon_url}
                            npcType={template.npc_type}
                            className="npc-picker-mark"
                          />
                          <span className="npc-picker-name">
                            {template.name}
                            {template.npc_type ? ` (${template.npc_type})` : ''}
                          </span>
                        </button>
                        <div className="npc-picker-qty" role="group" aria-label={`Anzahl ${template.name}`}>
                          <button
                            type="button"
                            aria-label={`Weniger ${template.name}`}
                            disabled={count <= 0}
                            onClick={() => setCount(template.id, count - 1)}
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min={0}
                            max={MAX_COPIES}
                            inputMode="numeric"
                            aria-label={`Anzahl ${template.name}`}
                            value={count}
                            onChange={(event) => setCount(template.id, Number(event.target.value))}
                          />
                          <button
                            type="button"
                            aria-label={`Mehr ${template.name}`}
                            disabled={count >= MAX_COPIES}
                            onClick={() => setCount(template.id, count + 1)}
                          >
                            +
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="modal-footer npc-picker-footer">
              <button
                type="button"
                className="medieval-button create-btn npc-picker-add"
                disabled={busy || totalSelected === 0}
                onClick={() => void addSelected()}
              >
                {totalSelected > 1
                  ? `${totalSelected} Gegner hinzufügen`
                  : 'Hinzufügen'}
              </button>
              <button type="button" className="medieval-button cancel-btn" onClick={closePicker}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
      {hpEdit && (
        <VitalSliderDialog
          title="❤️ HP ändern"
          name={hpEdit.name}
          initial={npcVitals(hpEdit).health}
          max={npcVitals(hpEdit).maxHealth}
          onClose={() => setHpEdit(null)}
          onApply={(value) => { void saveHealth(value); }}
        />
      )}
      {toRemove && (
        <ConfirmDialog
          title="Gegner entfernen?"
          message={`„${toRemove.name}“ aus diesem Spiel nehmen? Die Vorlage bleibt erhalten.`}
          confirmLabel="Entfernen"
          danger
          onCancel={() => setToRemove(null)}
          onConfirm={() => {
            void removeNpcFromGame(gameId, toRemove.id)
              .then(() => load())
              .finally(() => setToRemove(null));
          }}
        />
      )}
    </section>
  );
}
